/*
 * ZEEKO Zoomi PDP behaviour.
 *
 * Everything here is a custom element, so Shopify's Theme Editor gets correct
 * re-initialisation for free: when a section is re-rendered the new elements are
 * upgraded and connectedCallback() runs again. No load-once bootstrapping.
 */
(function () {
  'use strict';

  var isReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function define(name, ctor) {
    if (!customElements.get(name)) customElements.define(name, ctor);
  }

  /* ---------------------------------------------------------------- gallery */
  class ZeekoGallery extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('[data-zk-slide]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-zk-thumb]'));
      this.counter = this.querySelector('[data-zk-counter]');
      if (!this.slides.length) return;

      this.index = this.slides.findIndex(function (s) { return s.hasAttribute('data-active'); });
      if (this.index < 0) this.index = 0;

      this._onClick = this.handleClick.bind(this);
      this._onKey = this.handleKey.bind(this);
      this._onVariant = this.handleVariant.bind(this);

      this.addEventListener('click', this._onClick);
      this.addEventListener('keydown', this._onKey);
      document.addEventListener('zeeko:variant:change', this._onVariant);

      this.show(this.index);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onClick);
      this.removeEventListener('keydown', this._onKey);
      document.removeEventListener('zeeko:variant:change', this._onVariant);
    }

    handleClick(event) {
      var thumb = event.target.closest('[data-zk-thumb]');
      if (thumb) { this.show(this.thumbs.indexOf(thumb)); return; }
      var nav = event.target.closest('[data-zk-nav]');
      if (nav) this.step(nav.getAttribute('data-zk-nav') === 'next' ? 1 : -1);
    }

    handleKey(event) {
      if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
      if (!event.target.closest('[data-zk-thumb], [data-zk-nav]')) return;
      event.preventDefault();
      this.step(event.key === 'ArrowRight' ? 1 : -1);
    }

    handleVariant(event) {
      var mediaId = event.detail && event.detail.featuredMediaId;
      if (!mediaId) return;
      var match = this.slides.findIndex(function (s) {
        return s.getAttribute('data-media-id') === String(mediaId);
      });
      if (match > -1) this.show(match);
    }

    step(delta) {
      this.show((this.index + delta + this.slides.length) % this.slides.length);
    }

    show(index) {
      if (index < 0 || index >= this.slides.length) return;
      this.index = index;
      this.slides.forEach(function (slide, i) {
        if (i === index) slide.setAttribute('data-active', '');
        else slide.removeAttribute('data-active');
        slide.setAttribute('aria-hidden', i === index ? 'false' : 'true');
      });
      this.thumbs.forEach(function (thumb, i) {
        thumb.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
      if (this.counter) {
        this.counter.textContent = (index + 1) + ' / ' + this.slides.length;
      }
    }
  }

  /* --------------------------------------------------------------- purchase */
  class ZeekoPurchase extends HTMLElement {
    connectedCallback() {
      var data = this.querySelector('[data-zk-variants]');
      this.variants = [];
      if (data) {
        try { this.variants = JSON.parse(data.textContent) || []; } catch (e) { this.variants = []; }
      }

      this.variantInput = this.querySelector('[data-zk-variant-id]');
      this.qtyInput = this.querySelector('[data-zk-qty]');
      this.priceEl = this.querySelector('[data-zk-price]');
      this.wasEl = this.querySelector('[data-zk-was]');
      this.saveEl = this.querySelector('[data-zk-save]');
      this.atcBtn = this.querySelector('[data-zk-atc] button');
      this.atcAmount = this.querySelector('[data-zk-atc-amount]');
      this.atcLabel = this.querySelector('[data-zk-atc-label]');
      this.stockEl = this.querySelector('[data-zk-stock]');

      this._onChange = this.handleChange.bind(this);
      this._onClick = this.handleClick.bind(this);
      this._onInput = this.syncTierFromQty.bind(this);

      this.addEventListener('change', this._onChange);
      this.addEventListener('click', this._onClick);
      if (this.qtyInput) this.qtyInput.addEventListener('input', this._onInput);

      this.syncTierFromQty();
      this.publish();
    }

    disconnectedCallback() {
      this.removeEventListener('change', this._onChange);
      this.removeEventListener('click', this._onClick);
      if (this.qtyInput) this.qtyInput.removeEventListener('input', this._onInput);
    }

    handleChange(event) {
      if (event.target.matches('[data-zk-option]')) { this.selectVariant(); return; }
      if (event.target.matches('[data-zk-tier]')) {
        var qty = parseInt(event.target.getAttribute('data-zk-tier'), 10);
        if (this.qtyInput && qty > 0) this.qtyInput.value = qty;
      }
    }

    handleClick(event) {
      var step = event.target.closest('[data-zk-step]');
      if (!step || !this.qtyInput) return;
      var delta = step.getAttribute('data-zk-step') === 'up' ? 1 : -1;
      var min = parseInt(this.qtyInput.min, 10) || 1;
      var next = (parseInt(this.qtyInput.value, 10) || min) + delta;
      this.qtyInput.value = Math.max(min, next);
      this.syncTierFromQty();
    }

    syncTierFromQty() {
      if (!this.qtyInput) return;
      var qty = parseInt(this.qtyInput.value, 10) || 1;
      var tiers = Array.from(this.querySelectorAll('[data-zk-tier]'));
      if (!tiers.length) return;
      var best = null;
      tiers.forEach(function (tier) {
        var n = parseInt(tier.getAttribute('data-zk-tier'), 10);
        if (n <= qty && (!best || n > parseInt(best.getAttribute('data-zk-tier'), 10))) best = tier;
      });
      if (best && !best.checked) best.checked = true;
    }

    selectVariant() {
      var chosen = Array.from(this.querySelectorAll('[data-zk-option]:checked')).map(function (input) {
        return { index: parseInt(input.getAttribute('data-zk-option'), 10), value: input.value };
      });
      if (!chosen.length) return;

      var match = this.variants.find(function (variant) {
        return chosen.every(function (c) { return variant.options[c.index] === c.value; });
      });
      if (!match) return;

      if (this.variantInput) this.variantInput.value = match.id;
      this.render(match);
      this.updateUrl(match.id);
      this.publish(match);
    }

    render(variant) {
      this.querySelectorAll('[data-zk-option-label]').forEach(function (label) {
        var i = parseInt(label.getAttribute('data-zk-option-label'), 10);
        label.textContent = variant.options[i];
      });

      if (this.priceEl) this.priceEl.textContent = variant.price;
      if (this.wasEl) {
        this.wasEl.textContent = variant.compare_at || '';
        this.wasEl.hidden = !variant.compare_at;
      }
      if (this.saveEl) {
        this.saveEl.textContent = variant.save_text || '';
        this.saveEl.hidden = !variant.save_text;
      }
      if (this.atcAmount) {
        this.atcAmount.textContent = variant.price;
        this.atcAmount.hidden = !variant.available;
      }
      if (this.atcBtn) this.atcBtn.disabled = !variant.available;

      /* Labels live on the host element: Horizon's add-to-cart-button snippet
         renders the button itself and takes no extra attributes. */
      var addLabel = this.getAttribute('data-label-add');
      var soldLabel = this.getAttribute('data-label-sold');
      if (this.atcLabel && addLabel && soldLabel) {
        this.atcLabel.textContent = variant.available ? addLabel : soldLabel;
      }

      if (this.stockEl) {
        this.stockEl.hidden = !variant.low_stock_text;
        var text = this.stockEl.querySelector('[data-zk-stock-text]');
        if (text && variant.low_stock_text) text.textContent = variant.low_stock_text;
      }
    }

    updateUrl(id) {
      if (!window.history || !window.history.replaceState) return;
      var url = new URL(window.location.href);
      url.searchParams.set('variant', id);
      window.history.replaceState({}, '', url.toString());
    }

    publish(variant) {
      var current = variant || this.variants.find(function (v) { return v.selected; }) || this.variants[0];
      if (!current) return;
      document.dispatchEvent(new CustomEvent('zeeko:variant:change', {
        detail: {
          id: current.id,
          price: current.price,
          available: current.available,
          featuredMediaId: current.featured_media_id
        }
      }));
    }
  }

  /* -------------------------------------------------------------- countdown */
  class ZeekoCountdown extends HTMLElement {
    connectedCallback() {
      var end = this.getAttribute('data-end');
      this.endTime = end ? Date.parse(end) : NaN;
      if (isNaN(this.endTime) || this.endTime <= Date.now()) { this.hidden = true; return; }
      this.hidden = false;
      this.fields = {
        d: this.querySelector('[data-zk-cd="d"]'),
        h: this.querySelector('[data-zk-cd="h"]'),
        m: this.querySelector('[data-zk-cd="m"]'),
        s: this.querySelector('[data-zk-cd="s"]')
      };
      this.tick();
      this.timer = setInterval(this.tick.bind(this), 1000);
    }

    disconnectedCallback() { clearInterval(this.timer); }

    tick() {
      var left = this.endTime - Date.now();
      if (left <= 0) { clearInterval(this.timer); this.hidden = true; return; }
      var s = Math.floor(left / 1000);
      var pad = function (n) { return String(n).padStart(2, '0'); };
      if (this.fields.d) this.fields.d.textContent = pad(Math.floor(s / 86400));
      if (this.fields.h) this.fields.h.textContent = pad(Math.floor(s % 86400 / 3600));
      if (this.fields.m) this.fields.m.textContent = pad(Math.floor(s % 3600 / 60));
      if (this.fields.s) this.fields.s.textContent = pad(s % 60);
    }
  }

  /* ------------------------------------------------------------- sticky atc */
  class ZeekoStickyAtc extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-zk-sticky-add]');
      this.priceEl = this.querySelector('[data-zk-sticky-price]');

      this._onClick = this.handleClick.bind(this);
      this._onVariant = this.handleVariant.bind(this);
      this._onSectionLoad = this.bindTarget.bind(this);

      if (this.button) this.button.addEventListener('click', this._onClick);
      document.addEventListener('zeeko:variant:change', this._onVariant);
      document.addEventListener('shopify:section:load', this._onSectionLoad);

      this.bindTarget();
    }

    disconnectedCallback() {
      if (this.button) this.button.removeEventListener('click', this._onClick);
      document.removeEventListener('zeeko:variant:change', this._onVariant);
      document.removeEventListener('shopify:section:load', this._onSectionLoad);
      if (this.observer) this.observer.disconnect();
    }

    /* The primary CTA lives in a different section, so re-resolve it whenever
       the Theme Editor swaps that section out. */
    bindTarget() {
      if (this.observer) this.observer.disconnect();
      this.form = document.querySelector('[data-zk-primary-form]');
      var anchor = document.querySelector('[data-zk-atc]');
      if (!anchor || !('IntersectionObserver' in window)) return;

      var self = this;
      this.observer = new IntersectionObserver(function (entries) {
        var scrolledPast = entries[0].boundingClientRect.top < 0;
        if (!entries[0].isIntersecting && scrolledPast) self.setAttribute('data-visible', '');
        else self.removeAttribute('data-visible');
      }, { threshold: 0 });
      this.observer.observe(anchor);
    }

    handleVariant(event) {
      if (this.priceEl && event.detail && event.detail.price) {
        this.priceEl.textContent = event.detail.price;
      }
      if (this.button && event.detail) this.button.disabled = !event.detail.available;
    }

    /* Submitting the real product form keeps one source of truth for the
       selected variant and lets the theme's own cart handling take over. */
    handleClick() {
      if (!this.form) this.bindTarget();
      if (!this.form) return;
      if (typeof this.form.requestSubmit === 'function') this.form.requestSubmit();
      else this.form.submit();
    }
  }

  /* ------------------------------------------------------------ scroll link */
  class ZeekoScrollLink extends HTMLElement {
    connectedCallback() {
      this._onClick = function (event) {
        var trigger = event.target.closest('[data-zk-scroll-to]');
        if (!trigger) return;
        var target = document.querySelector(trigger.getAttribute('data-zk-scroll-to'));
        if (!target) return;
        event.preventDefault();
        target.scrollIntoView({ behavior: isReduced ? 'auto' : 'smooth', block: 'start' });
      };
      this.addEventListener('click', this._onClick);
    }
    disconnectedCallback() { this.removeEventListener('click', this._onClick); }
  }

  define('zeeko-gallery', ZeekoGallery);
  define('zeeko-purchase', ZeekoPurchase);
  define('zeeko-countdown', ZeekoCountdown);
  define('zeeko-sticky-atc', ZeekoStickyAtc);
  define('zeeko-scroll-link', ZeekoScrollLink);
})();
